/**
 * FinancialLogicTree - Hierarchical visualization of Financial Deep Dive analysis
 *
 * Displays the financial research process as a "Living Blueprint" that grows:
 * - User → Data Anchor → Bull/Bear Teams (parallel) → Debate → Mediator → Kill Sheet
 * - Live streaming text shows real-time agent activity
 * - SVG Bezier curves for connector lines (matching LogicTree.tsx)
 * - Auto-scroll to keep active nodes in view
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { InfoTooltip, Tooltip } from '@/components/ui/Tooltip';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import {
  User,
  Database,
  TrendingUp,
  TrendingDown,
  Scale,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Loader2,
  Zap,
  Brain,
  Sparkles,
  Target,
  X,
  Clock,
  FileText,
  ExternalLink,
  Link2,
  Expand,
  Minimize2,
  Microscope,
  BarChart3,
  AlertTriangle,
} from 'lucide-react';
import { MarkdownContent } from './MarkdownContent';
import { ExportMenu } from './ExportMenu';
import { handleFinancialExport, type FinancialExportOptions } from '@/utils/exportUtils';
import { getProviderLogo } from '@/utils/providerUtils';
import type { FinancialKillSheet, AnchoredSnapshot, ComparisonKillSheet } from './FinancialAnalysisPanel';
import type { ExportFormat } from '@/types/researchModes';

// ============================================================================
// TYPES
// ============================================================================

export type FinancialPhase =
  | 'idle'
  | 'fetching_data'
  | 'bull_research'
  | 'bear_research'
  | 'debating'
  | 'compiling'
  | 'concluded'
  | 'error';

export type FinancialResearchDepth = 'fast' | 'deep';

/** A citation extracted from agent output */
export interface FinancialCitation {
  id: string;        // e.g., "S1", "S2", "ANCHOR"
  source_type: string; // "FMP", "SEC", "NEWS", "ANCHOR", "WEB"
  title: string;     // Description of what was cited
  url?: string;      // URL if available
  published_at?: string; // Publication date/timestamp if known
}

export interface FinancialAgentProgress {
  side: 'bull' | 'bear';
  index: number;
  roleId: string;
  roleName: string;
  model: string;
  provider: 'anthropic' | 'openai' | 'google' | 'deepseek';
  status: 'idle' | 'researching' | 'complete' | 'error';
  output?: string;
  citationCount?: number;
  citations?: FinancialCitation[]; // Full citation details with URLs and timestamps
  latency?: number;
  retryCount?: number;        // Number of retry attempts made with fallback models
  lastFailedModel?: string;   // The last model that failed before successful retry
}

export interface DebateMessage {
  side: 'bull' | 'bear' | 'mediator';
  round?: number;
  text: string;
  /** In comparison mode, shows the ticker name instead of BULL/BEAR */
  label?: string;
  timestamp: number;
}

export interface MediatorCompilation {
  bullIntensity: number; // 0-100
  bearIntensity: number; // 0-100
  bullArguments: string[];
  bearArguments: string[];
  verdict: string;
}

/** Data for fallback when no ticker is identified */
export interface FallbackData {
  reason: string;
  suggestion: string;
  originalQuery: string;
}

export interface FinancialStreamingState {
  phase: FinancialPhase;
  ticker: string;
  depth: FinancialResearchDepth;
  hardDataSnapshot?: AnchoredSnapshot;
  bullAgents: FinancialAgentProgress[];
  bearAgents: FinancialAgentProgress[];
  debateMessages: DebateMessage[];
  debateRound: number;
  /**
   * In adversarial debate (financial mode): This is the mediator's CONFIDENCE
   * in the winning side, NOT agreement between agents. Higher = clearer winner.
   * In consensus debate: This represents actual agreement between agents.
   */
  agreementScore: number;
  mediatorCompilation?: MediatorCompilation;
  killSheet?: FinancialKillSheet;
  error?: string;
  fallbackData?: FallbackData;
  // Comparison mode fields
  isComparison?: boolean;
  comparisonTickerA?: string;
  comparisonTickerB?: string;
  comparisonKillSheet?: ComparisonKillSheet;
  comparisonSnapshotA?: AnchoredSnapshot;
  comparisonSnapshotB?: AnchoredSnapshot;
  // Per-stock analysis tracking (comparison mode)
  activeStockTicker?: string;
  stockABullAgents?: FinancialAgentProgress[];
  stockABearAgents?: FinancialAgentProgress[];
  stockBBullAgents?: FinancialAgentProgress[];
  stockBBearAgents?: FinancialAgentProgress[];
  agentsPerStock?: number;
  /** Warnings to display (e.g., 3+ ticker limitation) */
  warnings?: string[];
  // Convergence & echo chamber data from debate
  convergenceTrajectory?: number[];
  initialDivergence?: number;
  echoChamberedDetected?: boolean;
  // Credit cost from orchestrator done event
  totalCreditsUsed?: number;
  // Insufficient credits error from backend
  insufficientCredits?: { required: number; available: number };
}

export interface FinancialLogicTreeProps {
  state: FinancialStreamingState;
  query: string;
  className?: string;
  onNewResearch?: () => void;
  /** When true, hides export menus and action buttons (used in showcase/landing page) */
  hideActions?: boolean;
  /** Callback to switch to general research mode with the original query */
  onSwitchToGeneralResearch?: (query: string) => void;
  /** Callback to start financial analysis with a specific ticker */
  onAnalyzeTicker?: (ticker: string) => void;
}

// Provider colors (text variant) for agent nodes
const PROVIDER_TEXT_COLORS: Record<string, string> = {
  openai: 'text-emerald-400',
  anthropic: 'text-orange-400',
  google: 'text-blue-400',
  deepseek: 'text-purple-400',
};

// Depth config display
const DEPTH_CONFIGS: Record<FinancialResearchDepth, { label: string; agents: string; debates: number }> = {
  fast: { label: 'Fast', agents: '2+2', debates: 1 },
  deep: { label: 'Deep', agents: '3+3', debates: 2 },
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Financial Header */
function FinancialHeader({
  depth,
  ticker,
  phase,
  isSmallScreen,
  isComparison,
}: {
  depth: FinancialResearchDepth;
  ticker: string;
  phase: FinancialPhase;
  isSmallScreen: boolean;
  isComparison?: boolean;
}) {
  const depthConfig = DEPTH_CONFIGS[depth];
  const isActive = phase !== 'idle' && phase !== 'concluded' && phase !== 'error';

  return (
    <div
      className={cn(
        'border-b border-border-subtle bg-bar-bg/50 flex-shrink-0',
        isSmallScreen ? 'px-3 py-3' : 'px-6 py-4'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div
          className={cn(
            'flex items-center gap-1.5 sm:gap-2 font-mono text-text-muted uppercase tracking-wider',
            isSmallScreen ? 'text-xs' : 'text-sm'
          )}
        >
          <Scale className={cn('text-amber-500', isSmallScreen ? 'w-3.5 h-3.5' : 'w-4 h-4')} />
          <span className="flex items-center gap-1.5">
            {isComparison ? 'Financial Comparison' : 'Financial Deep Dive'}
            <InfoTooltip
              content="AI teams argue Bull (buy) vs Bear (sell) cases, then a mediator eliminates weak arguments to reach a final investment stance."
              position="bottom"
            />
            {ticker && (
              <span className="px-2 py-0.5 bg-amber-500/20 rounded text-amber-400 font-bold">
                {ticker}
              </span>
            )}
          </span>
          {isActive && (
            <span className={cn(
              'flex items-center gap-1 sm:gap-1.5 text-verdict-primary',
              isSmallScreen ? 'ml-1' : 'ml-2'
            )}>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="hidden sm:inline">Processing...</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-light/60 border border-border/50',
            isSmallScreen ? 'text-[10px]' : 'text-xs'
          )}>
            <Microscope className={cn(
              'text-verdict-primary',
              isSmallScreen ? 'w-3 h-3' : 'w-3.5 h-3.5'
            )} />
            <span className="text-text-secondary">
              {depthConfig.label}
            </span>
            <span className="text-text-muted">|</span>
            <span className="text-text-muted">
              {depthConfig.agents} agents
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** SVG Connector Line between nodes - matches LogicTree.tsx style */
function ConnectorLine({
  isBranching = false,
  isMerging = false,
  taskCount = 1,
  isAnimating = false,
}: {
  isBranching?: boolean;
  isMerging?: boolean;
  taskCount?: number;
  isAnimating?: boolean;
}) {
  if (isBranching || isMerging) {
    // Calculate width based on task count - wider spread for more tasks
    const svgWidth = Math.max(300, taskCount * 100);
    const startX = svgWidth / 2;
    // Spread endpoints evenly across the width
    const spread = Math.min(svgWidth - 100, (taskCount - 1) * 100);

    return (
      <div className="flex justify-center py-2">
        <svg width={svgWidth} height="40" className="text-text-muted">
          {Array.from({ length: taskCount }).map((_, i) => {
            // Calculate endpoint X position - evenly distributed
            const endX =
              taskCount === 1
                ? startX
                : startX - spread / 2 + i * (spread / Math.max(taskCount - 1, 1));
            const midY = 20;
            return (
              <path
                key={i}
                d={
                  isBranching
                    ? `M ${startX} 0 Q ${startX} ${midY} ${endX} 40`
                    : `M ${endX} 0 Q ${endX} ${midY} ${startX} 40`
                }
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4 2"
                className={cn(
                  'transition-all duration-500',
                  isAnimating ? 'animate-pulse opacity-100' : 'opacity-70'
                )}
                style={{
                  animation: isAnimating ? `drawLine 0.5s ease-out ${i * 0.2}s forwards` : undefined,
                }}
              />
            );
          })}
        </svg>
      </div>
    );
  }

  // Simple vertical connector - matches LogicTree.tsx style
  return (
    <div className="flex justify-center py-1">
      <div className="w-0.5 h-6 bg-gradient-to-b from-border-light to-border" />
    </div>
  );
}

/** User Query Node */
function UserQueryNode({
  query,
  isActive,
  isSmallScreen,
}: {
  query: string;
  isActive: boolean;
  isSmallScreen: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const truncated = query.length > 120;

  return (
    <div
      className={cn(
        'relative flex items-start border transition-all duration-500 overflow-hidden',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'gap-2 p-3' : 'gap-3 p-4',
        isActive
          ? 'from-blue-500/15 to-blue-600/5 border-blue-500/40 shadow-lg shadow-blue-500/10'
          : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
      )}
    >
      <div
        className={cn(
          'relative rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0',
          isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
          isActive ? 'bg-blue-500/25 shadow-inner' : 'bg-surface-light/80'
        )}
      >
        <User
          className={cn(
            'transition-all duration-300',
            isSmallScreen ? 'w-4 h-4' : 'w-5 h-5',
            isActive ? 'text-blue-400' : 'text-text-muted'
          )}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn('font-mono uppercase tracking-wider', isSmallScreen ? 'text-xs' : 'text-sm', 'text-blue-400')}>
          USER
        </div>
        <p className={cn(
          'text-text-primary break-words',
          isSmallScreen ? 'text-xs' : 'text-sm',
          !expanded && truncated && 'line-clamp-2'
        )}>"{query}"</p>
        {truncated && (
          <button
            className="mt-1 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
}

/** Data Anchor Node - Fetches market data and assigns agents */
function DataAnchorNode({
  snapshot,
  isActive,
  isComplete,
  bullAgentCount,
  bearAgentCount,
  isSmallScreen,
}: {
  snapshot?: AnchoredSnapshot;
  isActive: boolean;
  isComplete: boolean;
  bullAgentCount: number;
  bearAgentCount: number;
  isSmallScreen: boolean;
}) {
  // Expanded by default so users can see all data being considered
  const [showDetails, setShowDetails] = useState(true);

  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate analyst sentiment color
  const getSentimentColor = (consensus?: string) => {
    if (!consensus) return 'text-text-secondary';
    const lower = consensus.toLowerCase();
    if (lower.includes('strong buy') || lower === 'buy') return 'text-green-400';
    if (lower.includes('sell')) return 'text-red-400';
    return 'text-amber-400';
  };

  return (
    <div
      className={cn(
        'relative border transition-all duration-500',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'p-3' : 'p-4',
        isActive
          ? 'from-purple-500/15 to-purple-600/5 border-purple-500/40 shadow-lg shadow-purple-500/10'
          : isComplete
          ? 'from-purple-500/10 to-purple-600/5 border-purple-500/30'
          : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
      )}
    >
      {/* Header with Data Source and Timestamp */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className={cn(
            'rounded-lg flex items-center justify-center',
            isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
            isActive ? 'bg-purple-500/25' : isComplete ? 'bg-purple-500/20' : 'bg-surface-light/80'
          )}
        >
          {isActive ? (
            <Loader2 className={cn('animate-spin text-purple-400', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
          ) : (
            <Database className={cn(isComplete ? 'text-purple-400' : 'text-text-muted', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
          )}
        </div>
        <div className="flex-1">
          <div className={cn('font-mono uppercase tracking-wider text-purple-400', isSmallScreen ? 'text-xs' : 'text-sm')}>
            DATA ANCHOR
          </div>
          <p className="text-xs text-text-muted">
            {snapshot?.provider || 'FMP/Finnhub'} | As of: {formatDate(snapshot?.asOf)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isComplete && snapshot && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="px-2 py-1 rounded text-xs bg-surface-light text-text-secondary hover:bg-surface-raised hover:text-text-primary transition-colors flex items-center gap-1"
            >
              {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showDetails ? 'Less' : 'More'}
            </button>
          )}
          <Tooltip content={isActive ? 'Fetching live market data' : isComplete ? 'Market snapshot ready' : 'Waiting to fetch data'} position="top">
            <div className={cn('px-2 py-1 rounded text-xs cursor-help', isComplete ? 'bg-purple-500/20 text-purple-400' : 'bg-surface-light text-text-muted')}>
              {isActive ? 'Fetching...' : isComplete ? 'Ready' : 'Waiting'}
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Market Data Grid */}
      {snapshot && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm mb-3">
          <DataCell label="Price" value={snapshot.priceFormatted || (snapshot.price ? `$${snapshot.price.toFixed(2)}` : 'N/A')} />
          <DataCell label="Mkt Cap" value={snapshot.marketCapFormatted || formatMarketCap(snapshot.marketCap) || 'N/A'} />
          <DataCell label="P/E" value={snapshot.peFormatted || (snapshot.peTTM ? snapshot.peTTM.toFixed(1) : '--')} />
          <DataCell label="EPS" value={snapshot.epsFormatted || (snapshot.epsTTM ? `$${snapshot.epsTTM.toFixed(2)}` : '--')} />
          <DataCell label="Change" value={snapshot.changePercentFormatted || (snapshot.changePercent != null ? `${snapshot.changePercent.toFixed(2)}%` : 'N/A')} highlight={snapshot.changePercent} />
        </div>
      )}

      {/* Expanded Details Section */}
      {showDetails && snapshot && (
        <div className="space-y-3 border-t border-border pt-3">
          {/* Fallback when no Yahoo Finance data is available */}
          {!snapshot.yfRecommendations && !snapshot.recentNews?.length && !snapshot.altmanZScore && !snapshot.historicalData && (
            <div className="text-xs text-text-muted italic py-2">
              Analyst data and news unavailable — Yahoo Finance may be rate-limited. Financial health scores and historical data will appear when available.
            </div>
          )}
          {/* Analyst Sentiment Section */}
          {snapshot.yfRecommendations && (
            <div>
              <div className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <Target className="w-3 h-3" />
                Analyst Sentiment ({snapshot.numAnalysts || snapshot.yfRecommendations.total} analysts)
                <span className="text-text-muted font-normal">via Yahoo Finance</span>
              </div>
              <div className="grid grid-cols-5 gap-1 text-xs mb-2">
                <div className="bg-green-500/20 rounded p-1.5 text-center">
                  <div className="text-green-400 font-bold">{snapshot.yfRecommendations.strong_buy}</div>
                  <div className="text-text-muted text-[10px]">Strong Buy</div>
                </div>
                <div className="bg-green-500/10 rounded p-1.5 text-center">
                  <div className="text-green-400 font-bold">{snapshot.yfRecommendations.buy}</div>
                  <div className="text-text-muted text-[10px]">Buy</div>
                </div>
                <div className="bg-surface-raised/50 rounded p-1.5 text-center">
                  <div className="text-text-secondary font-bold">{snapshot.yfRecommendations.hold}</div>
                  <div className="text-text-muted text-[10px]">Hold</div>
                </div>
                <div className="bg-red-500/10 rounded p-1.5 text-center">
                  <div className="text-red-400 font-bold">{snapshot.yfRecommendations.sell}</div>
                  <div className="text-text-muted text-[10px]">Sell</div>
                </div>
                <div className="bg-red-500/20 rounded p-1.5 text-center">
                  <div className="text-red-400 font-bold">{snapshot.yfRecommendations.strong_sell}</div>
                  <div className="text-text-muted text-[10px]">Strong Sell</div>
                </div>
              </div>
              <div className="text-xs text-text-secondary">
                Consensus: <span className={getSentimentColor(snapshot.consensusRating || snapshot.yfRecommendations.consensus)}>{snapshot.consensusRating || snapshot.yfRecommendations.consensus}</span>
                {snapshot.avgPriceTarget && (
                  <>
                    {' '}| Target: ${snapshot.avgPriceTarget.toFixed(2)}
                    {snapshot.priceTargetUpside != null && (
                      <span className={snapshot.priceTargetUpside >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {' '}({snapshot.priceTargetUpside >= 0 ? '+' : ''}{snapshot.priceTargetUpside.toFixed(1)}%)
                      </span>
                    )}
                  </>
                )}
              </div>
              {snapshot.yfPriceTargets && (
                <div className="text-xs text-text-muted mt-1">
                  Range: ${snapshot.yfPriceTargets.low != null ? snapshot.yfPriceTargets.low.toFixed(2) : '--'} - ${snapshot.yfPriceTargets.high != null ? snapshot.yfPriceTargets.high.toFixed(2) : '--'}
                  {snapshot.yfPriceTargets.median != null && <> | Median: ${snapshot.yfPriceTargets.median.toFixed(2)}</>}
                </div>
              )}
            </div>
          )}

          {/* Financial Health Scores Section */}
          {(snapshot.altmanZScore != null || snapshot.piotroskiScore != null) && (
            <div>
              <div className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <Zap className="w-3 h-3" />
                Financial Health Scores
                <span className="text-text-muted font-normal">via FMP</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {snapshot.altmanZScore != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">Altman Z-Score</div>
                    <div className="text-text-primary font-bold">{snapshot.altmanZScore.toFixed(2)}</div>
                    <div className={cn(
                      'text-[10px]',
                      snapshot.altmanZRisk === 'Safe' ? 'text-green-400' :
                      snapshot.altmanZRisk === 'Grey Zone' ? 'text-amber-400' : 'text-red-400'
                    )}>{snapshot.altmanZRisk || 'N/A'}</div>
                  </div>
                )}
                {snapshot.piotroskiScore != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">Piotroski Score</div>
                    <div className="text-text-primary font-bold">{snapshot.piotroskiScore}/9</div>
                    <div className={cn(
                      'text-[10px]',
                      snapshot.piotroskiStrength === 'Strong' ? 'text-green-400' :
                      snapshot.piotroskiStrength === 'Average' ? 'text-amber-400' : 'text-red-400'
                    )}>{snapshot.piotroskiStrength || 'N/A'}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Market Sentiment Indicators */}
          {snapshot.yfKeyStats && (
            <div>
              <div className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <Brain className="w-3 h-3" />
                Market Sentiment
                <span className="text-text-muted font-normal">via Yahoo Finance</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {snapshot.yfKeyStats.short_ratio != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">Short Ratio</div>
                    <div className="text-text-primary font-mono">{snapshot.yfKeyStats.short_ratio.toFixed(2)}</div>
                  </div>
                )}
                {snapshot.yfKeyStats.held_by_institutions != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">Institutional</div>
                    <div className="text-text-primary font-mono">{(snapshot.yfKeyStats.held_by_institutions * 100).toFixed(1)}%</div>
                  </div>
                )}
                {snapshot.yfKeyStats.held_by_insiders != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">Insider</div>
                    <div className="text-text-primary font-mono">{(snapshot.yfKeyStats.held_by_insiders * 100).toFixed(1)}%</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recent News Headlines */}
          {snapshot.recentNews && snapshot.recentNews.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <FileText className="w-3 h-3" />
                Recent News ({snapshot.recentNews.length} headlines)
                <span className="text-text-muted font-normal">
                  via {snapshot.recentNews[0]?.source === 'yahoo_finance' ? 'Yahoo Finance' : 'FMP'}
                </span>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {snapshot.recentNews.map((news, idx) => (
                  <div key={idx} className="text-xs text-text-secondary flex items-start gap-1.5 group">
                    <span className="text-text-muted mt-0.5">-</span>
                    <div className="flex-1 min-w-0">
                      {news.url || news.link ? (
                        <a
                          href={news.url || news.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-purple-400 transition-colors flex items-start gap-1"
                          title={news.title}
                        >
                          <span className="line-clamp-2">{news.title}</span>
                          <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                        </a>
                      ) : (
                        <span className="line-clamp-2" title={news.title}>{news.title}</span>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-text-muted mt-0.5">
                        {(news.site || news.publisher) && <span>{news.site || news.publisher}</span>}
                        {(news.publishedDate || news.published_date) && (
                          <span>{new Date(news.publishedDate || news.published_date || '').toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Historical Performance Section (5-Year) */}
          {snapshot.historicalData && (
            <div>
              <div className="text-xs font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <TrendingUp className="w-3 h-3" />
                Historical Performance (5Y)
                <span className="text-text-muted font-normal">via Yahoo Finance</span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs mb-2">
                {snapshot.historicalData.return_ytd != null && (
                  <div className="bg-surface-light/50 rounded p-2">
                    <div className="text-text-muted text-[10px]">YTD</div>
                    <div className={snapshot.historicalData.return_ytd >= 0 ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                      {snapshot.historicalData.return_ytd >= 0 ? '+' : ''}{snapshot.historicalData.return_ytd.toFixed(1)}%
                    </div>
                  </div>
                )}
                {snapshot.historicalData.return_1y != null && (
                  <div className="bg-surface-light/50 rounded p-2">
                    <div className="text-text-muted text-[10px]">1 Year</div>
                    <div className={snapshot.historicalData.return_1y >= 0 ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                      {snapshot.historicalData.return_1y >= 0 ? '+' : ''}{snapshot.historicalData.return_1y.toFixed(1)}%
                    </div>
                  </div>
                )}
                {snapshot.historicalData.return_3y != null && (
                  <div className="bg-surface-light/50 rounded p-2">
                    <div className="text-text-muted text-[10px]">3 Year</div>
                    <div className={snapshot.historicalData.return_3y >= 0 ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                      {snapshot.historicalData.return_3y >= 0 ? '+' : ''}{snapshot.historicalData.return_3y.toFixed(1)}%
                    </div>
                  </div>
                )}
                {snapshot.historicalData.return_5y != null && (
                  <div className="bg-surface-light/50 rounded p-2">
                    <div className="text-text-muted text-[10px]">5 Year</div>
                    <div className={snapshot.historicalData.return_5y >= 0 ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                      {snapshot.historicalData.return_5y >= 0 ? '+' : ''}{snapshot.historicalData.return_5y.toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {snapshot.historicalData.volatility_1y != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">Volatility (1Y)</div>
                    <div className="text-text-primary font-mono">{snapshot.historicalData.volatility_1y.toFixed(1)}%</div>
                  </div>
                )}
                {snapshot.historicalData.high_52w != null && snapshot.historicalData.low_52w != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">52W Range</div>
                    <div className="text-text-primary font-mono text-[11px]">${snapshot.historicalData.low_52w.toFixed(2)} - ${snapshot.historicalData.high_52w.toFixed(2)}</div>
                  </div>
                )}
                {snapshot.historicalData.avg_volume_3m != null && (
                  <div className="bg-surface/50 rounded p-2">
                    <div className="text-text-muted">Avg Vol (3M)</div>
                    <div className="text-text-primary font-mono text-[11px]">{(snapshot.historicalData.avg_volume_3m / 1e6).toFixed(1)}M</div>
                  </div>
                )}
              </div>
              <div className="text-[10px] text-text-muted mt-1">
                Data: {snapshot.historicalData.start_date} to {snapshot.historicalData.end_date} ({snapshot.historicalData.data_points} trading days)
              </div>
            </div>
          )}
        </div>
      )}

      {/* Agent Assignment */}
      {isComplete && (bullAgentCount > 0 || bearAgentCount > 0) && (
        <div className="text-xs text-text-muted border-t border-border pt-2 mt-2">
          <span className="text-purple-400">Agents assigned:</span>{' '}
          <Tooltip content="AI analysts arguing why this investment could succeed — growth catalysts, undervaluation, and positive momentum" position="top">
            <span className="text-green-400 cursor-help">{bullAgentCount} Bull</span>
          </Tooltip>{' '}
          <span className="text-text-muted">|</span>{' '}
          <Tooltip content="AI analysts arguing against — overvaluation risks, competitive threats, and negative trends" position="top">
            <span className="text-red-400 cursor-help">{bearAgentCount} Bear</span>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

/** Data Cell for market snapshot */
function DataCell({ label, value, highlight }: { label: string; value: string; highlight?: number }) {
  const colorClass = highlight !== undefined ? (highlight >= 0 ? 'text-green-400' : 'text-red-400') : 'text-text-primary';
  return (
    <div className="bg-surface/50 rounded-lg p-2">
      <span className="text-text-muted text-xs">{label}</span>
      <p className={cn('font-mono', colorClass)}>{value}</p>
    </div>
  );
}

/** Format market cap */
function formatMarketCap(value?: number): string {
  if (!value) return '--';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toLocaleString()}`;
}

/** Trending stocks to suggest when no ticker is identified */
const SUGGESTED_TICKERS = [
  { ticker: 'NVDA', name: 'NVIDIA', sector: 'AI/Semiconductors' },
  { ticker: 'AAPL', name: 'Apple', sector: 'Technology' },
  { ticker: 'MSFT', name: 'Microsoft', sector: 'Technology' },
  { ticker: 'GOOGL', name: 'Alphabet', sector: 'Technology' },
  { ticker: 'TSLA', name: 'Tesla', sector: 'EV/Energy' },
  { ticker: 'META', name: 'Meta', sector: 'Social/AI' },
];

/** No Ticker Fallback Component - Shows when query doesn't have a specific ticker */
function NoTickerFallback({
  fallbackData,
  originalQuery,
  onSwitchToGeneralResearch,
  onAnalyzeTicker,
  isSmallScreen,
}: {
  fallbackData: FallbackData;
  originalQuery: string;
  onSwitchToGeneralResearch?: (query: string) => void;
  onAnalyzeTicker?: (ticker: string) => void;
  isSmallScreen: boolean;
}) {
  return (
    <div
      className={cn(
        'relative border transition-all duration-500',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        'from-amber-500/10 to-orange-600/5 border-amber-500/30',
        isSmallScreen ? 'p-4' : 'p-6'
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className={cn(
            'rounded-lg flex items-center justify-center bg-amber-500/20',
            isSmallScreen ? 'w-10 h-10' : 'w-12 h-12'
          )}
        >
          <Target className={cn('text-amber-400', isSmallScreen ? 'w-5 h-5' : 'w-6 h-6')} />
        </div>
        <div className="flex-1">
          <h3 className={cn('font-semibold text-amber-400', isSmallScreen ? 'text-sm' : 'text-base')}>
            No Specific Ticker Identified
          </h3>
          <p className="text-text-secondary text-sm mt-1">
            {fallbackData.reason || 'Your query is about general investment advice rather than a specific stock.'}
          </p>
        </div>
      </div>

      {/* Suggestion */}
      <div className="bg-surface/50 rounded-lg p-4 mb-4">
        <p className="text-text-primary text-sm">
          <span className="text-amber-400 font-medium">Tip:</span>{' '}
          {fallbackData.suggestion || 'For stock-specific analysis, try asking about a particular company like "Analyze NVDA" or "Should I buy Apple stock?"'}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3">
        {/* Switch to General Research */}
        {onSwitchToGeneralResearch && (
          <button
            onClick={() => onSwitchToGeneralResearch(originalQuery)}
            className={cn(
              'w-full flex items-center justify-center gap-2 rounded-lg font-medium transition-all',
              'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400',
              'text-text-primary shadow-lg shadow-blue-500/20',
              isSmallScreen ? 'px-4 py-2.5 text-sm' : 'px-5 py-3'
            )}
          >
            <Sparkles className="w-4 h-4" />
            Switch to General Research
          </button>
        )}

        {/* Suggested Tickers */}
        {onAnalyzeTicker && (
          <div className="mt-2">
            <p className="text-xs text-text-muted uppercase tracking-wider mb-2">
              Or analyze a trending stock:
            </p>
            <div className={cn('grid gap-2', isSmallScreen ? 'grid-cols-2' : 'grid-cols-3')}>
              {SUGGESTED_TICKERS.map((stock) => (
                <button
                  key={stock.ticker}
                  onClick={() => onAnalyzeTicker(stock.ticker)}
                  className={cn(
                    'flex flex-col items-start rounded-lg border transition-all',
                    'bg-surface/50 border-border/50 hover:border-amber-500/50 hover:bg-surface-light/50',
                    isSmallScreen ? 'p-2' : 'p-3'
                  )}
                >
                  <span className="font-mono font-bold text-amber-400">${stock.ticker}</span>
                  <span className="text-xs text-text-secondary">{stock.name}</span>
                  <span className="text-[10px] text-text-muted">{stock.sector}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Bull/Bear Parallel Tasks Container */
function BullBearParallelTasks({
  bullAgents,
  bearAgents,
  phase,
  isSmallScreen,
  onAgentClick,
  isExpandedView,
  onToggleExpanded,
  isComparison,
  comparisonTickerA,
  comparisonTickerB,
}: {
  bullAgents: FinancialAgentProgress[];
  bearAgents: FinancialAgentProgress[];
  phase: FinancialPhase;
  isSmallScreen: boolean;
  onAgentClick?: (agent: FinancialAgentProgress) => void;
  isExpandedView?: boolean;
  onToggleExpanded?: () => void;
  isComparison?: boolean;
  comparisonTickerA?: string;
  comparisonTickerB?: string;
}) {
  const isBullActive = phase === 'bull_research';
  const isBearActive = phase === 'bear_research';
  const isBullComplete = ['bear_research', 'debating', 'compiling', 'concluded'].includes(phase);
  const isBearComplete = ['debating', 'compiling', 'concluded'].includes(phase);
  const allComplete = isBullComplete && isBearComplete;

  return (
    <div className="relative">
      {/* Parallel indicator badge with expand toggle */}
      <div
        className={cn(
          'absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-surface border border-amber-500/30 rounded font-mono text-amber-500/70 flex items-center gap-1 whitespace-nowrap',
          isSmallScreen ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
        )}
      >
        <Zap className={cn(isSmallScreen ? 'w-2.5 h-2.5' : 'w-3 h-3')} />
        Running in parallel (2 tasks)
        {allComplete && onToggleExpanded && (
          <Tooltip content={isExpandedView ? 'Collapse outputs' : 'Expand all agent outputs'} position="top">
          <button
            onClick={onToggleExpanded}
            className="ml-2 flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 rounded transition-colors"
          >
            {isExpandedView ? (
              <>
                <Minimize2 className="w-3 h-3" />
                Collapse
              </>
            ) : (
              <>
                <Expand className="w-3 h-3" />
                Expand All
              </>
            )}
          </button>
          </Tooltip>
        )}
      </div>

      {/* Container with dashed border */}
      <div
        className={cn(
          'grid md:grid-cols-2 gap-4 border-2 border-dashed border-amber-500/30 rounded-2xl',
          isSmallScreen ? 'p-3 pt-5' : 'p-4 pt-6'
        )}
      >
        {/* Bull Case / Team A / Stock A Analysis */}
        <TaskCard
          side="bull"
          agents={bullAgents}
          isActive={isBullActive}
          isComplete={isBullComplete}
          isSmallScreen={isSmallScreen}
          onAgentClick={onAgentClick}
          isExpandedView={isExpandedView}
          comparisonSide={isComparison ? 'a' : undefined}
          comparisonLabel={isComparison
            ? `${comparisonTickerA || 'A'} ANALYSIS`
            : undefined}
          comparisonDescription={isComparison
            ? `Bull + Bear analysis of ${comparisonTickerA || 'Ticker A'}`
            : undefined}
        />

        {/* Stock B Analysis */}
        <TaskCard
          side="bear"
          agents={bearAgents}
          isActive={isBearActive}
          isComplete={isBearComplete}
          isSmallScreen={isSmallScreen}
          onAgentClick={onAgentClick}
          isExpandedView={isExpandedView}
          comparisonSide={isComparison ? 'b' : undefined}
          comparisonLabel={isComparison
            ? `${comparisonTickerB || 'B'} ANALYSIS`
            : undefined}
          comparisonDescription={isComparison
            ? `Bull + Bear analysis of ${comparisonTickerB || 'Ticker B'}`
            : undefined}
        />
      </div>
    </div>
  );
}

/** Task Card (Bull or Bear) */
function TaskCard({
  side,
  agents,
  isActive,
  isComplete,
  isSmallScreen,
  onAgentClick,
  isExpandedView,
  comparisonSide,
  comparisonLabel,
  comparisonDescription,
}: {
  side: 'bull' | 'bear';
  agents: FinancialAgentProgress[];
  isActive: boolean;
  isComplete: boolean;
  isSmallScreen: boolean;
  onAgentClick?: (agent: FinancialAgentProgress) => void;
  isExpandedView?: boolean;
  comparisonSide?: 'a' | 'b';
  comparisonLabel?: string;
  comparisonDescription?: string;
}) {
  const isBull = side === 'bull';
  const isComparisonMode = !!comparisonSide;
  const isStockA = comparisonSide === 'a';

  // In comparison mode: amber for Stock A, blue for Stock B
  // In normal mode: green for bull, red for bear
  const Icon = isComparisonMode ? BarChart3 : (isBull ? TrendingUp : TrendingDown);
  const completedAgents = agents.filter((a) => a.status === 'complete').length;

  // Color schemes
  const colors = isComparisonMode
    ? isStockA
      ? { active: 'from-amber-500/15 to-amber-600/5 border-amber-500/40 shadow-lg shadow-amber-500/10',
          complete: 'from-amber-500/10 to-amber-600/5 border-amber-500/30',
          iconBg: 'bg-amber-500/20', iconText: 'text-amber-400', labelText: 'text-amber-400',
          badge: 'bg-amber-500/20 text-amber-400' }
      : { active: 'from-blue-500/15 to-blue-600/5 border-blue-500/40 shadow-lg shadow-blue-500/10',
          complete: 'from-blue-500/10 to-blue-600/5 border-blue-500/30',
          iconBg: 'bg-blue-500/20', iconText: 'text-blue-400', labelText: 'text-blue-400',
          badge: 'bg-blue-500/20 text-blue-400' }
    : isBull
      ? { active: 'from-green-500/15 to-green-600/5 border-green-500/40 shadow-lg shadow-green-500/10',
          complete: 'from-green-500/10 to-green-600/5 border-green-500/30',
          iconBg: 'bg-green-500/20', iconText: 'text-green-400', labelText: 'text-green-400',
          badge: 'bg-green-500/20 text-green-400' }
      : { active: 'from-red-500/15 to-red-600/5 border-red-500/40 shadow-lg shadow-red-500/10',
          complete: 'from-red-500/10 to-red-600/5 border-red-500/30',
          iconBg: 'bg-red-500/20', iconText: 'text-red-400', labelText: 'text-red-400',
          badge: 'bg-red-500/20 text-red-400' };

  return (
    <div
      className={cn(
        'relative border transition-all duration-500',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'p-3' : 'p-4',
        isActive
          ? colors.active
          : isComplete
          ? colors.complete
          : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className={cn(
            'rounded-lg flex items-center justify-center',
            isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
            colors.iconBg
          )}
        >
          <Icon className={cn(colors.iconText, isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
        </div>
        <div className="flex-1">
          <div className={cn('font-mono uppercase tracking-wider', colors.labelText, isSmallScreen ? 'text-xs' : 'text-sm')}>
            {comparisonLabel || (isBull ? 'BULL CASE' : 'BEAR CASE')}
          </div>
          <p className="text-xs text-text-muted">{comparisonDescription || `Task ${isBull ? '1' : '2'}: Build the ${isBull ? 'bullish' : 'bearish'} thesis`}</p>
        </div>
        <div className={cn('px-2 py-1 rounded text-xs', isComplete ? colors.badge : 'bg-surface-light text-text-muted')}>
          {completedAgents}/{agents.length}
        </div>
      </div>

      {/* Agent Cards - Expanded or Compact */}
      <div className={cn('space-y-2', isExpandedView && 'space-y-4')}>
        {agents.map((agent) => (
          isExpandedView ? (
            <ExpandedAgentCard
              key={`${agent.side}-${agent.index}`}
              agent={agent}
              isSmallScreen={isSmallScreen}
            />
          ) : (
            <FinancialAgentCard
              key={`${agent.side}-${agent.index}`}
              agent={agent}
              isSmallScreen={isSmallScreen}
              onAgentClick={onAgentClick}
            />
          )
        ))}
      </div>
    </div>
  );
}

/** Financial Agent Card */
function FinancialAgentCard({
  agent,
  isSmallScreen,
  onAgentClick,
}: {
  agent: FinancialAgentProgress;
  isSmallScreen: boolean;
  onAgentClick?: (agent: FinancialAgentProgress) => void;
}) {
  const providerColor = PROVIDER_TEXT_COLORS[agent.provider] || 'text-text-secondary';
  const isBull = agent.side === 'bull';
  const isClickable = agent.status === 'complete' && agent.output && onAgentClick;

  return (
    <div
      className={cn(
        'rounded-lg border transition-all',
        isSmallScreen ? 'p-2' : 'p-3',
        agent.status === 'researching'
          ? isBull
            ? 'bg-green-500/10 border-green-500/30'
            : 'bg-red-500/10 border-red-500/30'
          : agent.status === 'complete'
          ? 'bg-surface-light/50 border-border/50'
          : 'bg-surface/50 border-border',
        isClickable && 'cursor-pointer hover:border-border-light hover:bg-surface-light/70'
      )}
      onClick={() => isClickable && onAgentClick(agent)}
    >
      <div className="flex items-center gap-2">
        <Tooltip content={`AI model: ${agent.provider}`} position="top">
          <div className={cn('rounded flex items-center justify-center', isSmallScreen ? 'w-6 h-6' : 'w-8 h-8', 'bg-surface-light')}>
            <span className={providerColor}>{getProviderLogo(agent.provider, isSmallScreen ? 12 : 16)}</span>
          </div>
        </Tooltip>
        <div className="flex-1 min-w-0">
          <div className={cn('font-medium truncate', isSmallScreen ? 'text-xs' : 'text-sm', isBull ? 'text-green-400' : 'text-red-400')}>
            {agent.roleName}
          </div>
          <div className="text-[10px] text-text-muted truncate">{agent.model}</div>
        </div>
        <div className="flex items-center gap-1">
          {agent.status === 'researching' && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
          {agent.status === 'complete' && <CheckCircle2 className="w-3 h-3 text-green-400" />}
          {agent.citationCount !== undefined && agent.status === 'complete' && (
            <span className="text-[10px] text-text-muted">{agent.citationCount} cites</span>
          )}
        </div>
      </div>

      {/* Click hint when complete */}
      {isClickable && (
        <div className="mt-2 text-[10px] text-text-muted flex items-center gap-1">
          <ChevronDown className="w-3 h-3" />
          Click to view full output
        </div>
      )}
    </div>
  );
}

/** Expanded Agent Card - Shows full output and sources inline ("fallen out" view) */
function ExpandedAgentCard({
  agent,
  isSmallScreen,
}: {
  agent: FinancialAgentProgress;
  isSmallScreen: boolean;
}) {
  const providerColor = PROVIDER_TEXT_COLORS[agent.provider] || 'text-text-secondary';
  const isBull = agent.side === 'bull';

  return (
    <div
      className={cn(
        'rounded-lg border transition-all',
        isSmallScreen ? 'p-3' : 'p-4',
        isBull ? 'bg-surface/70 border-green-500/30' : 'bg-surface/70 border-red-500/30'
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
        <div className={cn('rounded flex items-center justify-center', 'w-8 h-8', 'bg-surface-light')}>
          <span className={providerColor}>{getProviderLogo(agent.provider, 16)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn('font-medium', 'text-sm', isBull ? 'text-green-400' : 'text-red-400')}>
            {agent.roleName}
          </div>
          <div className="text-[10px] text-text-muted">{agent.model}</div>
        </div>
        <div className="flex items-center gap-2">
          {agent.latency && (
            <span className="text-[10px] text-text-muted">
              {(agent.latency / 1000).toFixed(1)}s
            </span>
          )}
          {agent.status === 'complete' && <CheckCircle2 className="w-4 h-4 text-green-400" />}
          {agent.status === 'researching' && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
        </div>
      </div>

      {/* Full Output in Markdown */}
      {agent.output ? (
        <div className="prose prose-sm prose-invert max-w-none text-xs leading-relaxed mb-3">
          <MarkdownContent content={agent.output} />
        </div>
      ) : (
        <div className="text-xs text-text-muted italic mb-3">No output available</div>
      )}

      {/* Sources Section */}
      {agent.citations && agent.citations.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-3 h-3 text-blue-400" />
            <span className="text-[10px] font-semibold text-text-primary uppercase tracking-wider">
              Sources ({agent.citations.length})
            </span>
          </div>
          <div className="space-y-1.5">
            {agent.citations.map((citation, idx) => (
              <div key={idx} className="bg-surface-light/50 rounded p-2 text-[10px]">
                <div className="flex items-start gap-2">
                  <span className="font-mono text-blue-400 font-semibold">[{citation.id}]</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="px-1.5 py-0.5 bg-surface-raised rounded text-[9px] font-mono text-text-primary">
                        {citation.source_type}
                      </span>
                      {citation.published_at && (
                        <span className="flex items-center gap-1 text-text-muted">
                          <Clock className="w-2.5 h-2.5" />
                          {new Date(citation.published_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      )}
                    </div>
                    <p className="text-text-primary truncate">{citation.title}</p>
                    {citation.url && (
                      <a
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors mt-0.5"
                      >
                        <ExternalLink className="w-2.5 h-2.5" />
                        <span className="truncate">{new URL(citation.url).hostname}</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Convergence Sparkline — mini SVG showing agreement trajectory across rounds */
function ConvergenceSparkline({ data, className }: { data: number[]; className?: string }) {
  if (data.length < 2) return null;
  const width = 200;
  const height = 32;
  const padding = 2;
  const plotW = width - padding * 2;
  const plotH = height - padding * 2;

  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * plotW;
    const y = padding + (1 - v) * plotH; // invert: higher score = higher on chart
    return `${x},${y}`;
  }).join(' ');

  const isRising = data[data.length - 1] > data[0] + 0.05;
  const strokeColor = isRising ? '#10b981' : '#f59e0b'; // green if rising, amber if flat/falling

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <svg width={width} height={height} className="shrink-0" viewBox={`0 0 ${width} ${height}`}>
        {/* Baseline at 0.5 */}
        <line x1={padding} y1={height / 2} x2={width - padding} y2={height / 2} stroke="rgb(var(--color-border))" strokeWidth={0.5} strokeDasharray="4 2" />
        <polyline fill="none" stroke={strokeColor} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" points={points} />
        {/* Dot on last point */}
        {data.length > 0 && (
          <circle cx={padding + ((data.length - 1) / (data.length - 1)) * plotW} cy={padding + (1 - data[data.length - 1]) * plotH} r={3} fill={strokeColor} />
        )}
      </svg>
      <div className="text-[10px] font-mono text-text-secondary whitespace-nowrap">
        {(data[0] * 100).toFixed(0)}% → {(data[data.length - 1] * 100).toFixed(0)}%
      </div>
    </div>
  );
}

/** Debate Node */
function DebateNode({
  messages,
  round,
  agreementScore,
  isActive,
  isComplete,
  isSmallScreen,
  disableAutoScroll,
  isComparison,
  comparisonTickerA,
  comparisonTickerB,
  convergenceTrajectory,
  initialDivergence,
  echoChamberedDetected,
}: {
  messages: DebateMessage[];
  round: number;
  agreementScore: number;
  isActive: boolean;
  isComplete: boolean;
  isSmallScreen: boolean;
  disableAutoScroll?: boolean;
  isComparison?: boolean;
  comparisonTickerA?: string;
  comparisonTickerB?: string;
  convergenceTrajectory?: number[];
  initialDivergence?: number;
  echoChamberedDetected?: boolean;
}) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialRender = useRef(true);

  // Auto-scroll to latest message (disabled on landing page and initial render)
  useEffect(() => {
    if (disableAutoScroll) return;
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, disableAutoScroll]);

  return (
    <div
      className={cn(
        'relative border transition-all duration-500',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'p-3' : 'p-4',
        isActive
          ? 'from-amber-500/15 to-amber-600/5 border-amber-500/40 shadow-lg shadow-amber-500/10'
          : isComplete
          ? 'from-amber-500/10 to-amber-600/5 border-amber-500/30'
          : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('rounded-lg flex items-center justify-center bg-amber-500/20', isSmallScreen ? 'w-8 h-8' : 'w-10 h-10')}>
          <Scale className={cn('text-amber-400', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
        </div>
        <div className="flex-1">
          <div className={cn('font-mono uppercase tracking-wider text-amber-400', isSmallScreen ? 'text-xs' : 'text-sm')}>
            {isComparison ? 'COMPARATIVE DEBATE' : 'DEBATE'}
          </div>
          <p className="text-xs text-text-muted">
            {isComparison
              ? `${comparisonTickerA || 'A'} vs ${comparisonTickerB || 'B'} — advocate reconciliation`
              : 'Adversarial reconciliation until consensus'}
          </p>
        </div>
        <div className={cn('px-2 py-1 rounded text-xs', isActive ? 'bg-amber-500/20 text-amber-400' : 'bg-surface-light text-text-muted')}>
          Round {round || 1}
        </div>
      </div>

      {/* Debate Messages — grouped by round */}
      <div className="max-h-64 overflow-y-auto space-y-2 mb-3">
        {messages.length === 0 ? (
          <p className="text-xs text-text-muted text-center py-4">Debate messages will appear here...</p>
        ) : (() => {
          // Group messages by round
          const roundGroups = new Map<number, DebateMessage[]>();
          for (const msg of messages) {
            const r = msg.round ?? 0;
            if (!roundGroups.has(r)) roundGroups.set(r, []);
            roundGroups.get(r)!.push(msg);
          }
          const sortedRounds = Array.from(roundGroups.entries()).sort(([a], [b]) => a - b);
          return sortedRounds.map(([roundNum, msgs], groupIdx) => (
            <div key={roundNum}>
              {sortedRounds.length > 1 && (
                <div className="flex items-center gap-2 my-2">
                  <div className="h-px flex-1 bg-surface-raised/60" />
                  <span className="text-[9px] font-mono text-text-muted uppercase tracking-wider">
                    {groupIdx === 0 ? 'Initial Analysis' : `Debate Round ${roundNum}`}
                  </span>
                  <div className="h-px flex-1 bg-surface-raised/60" />
                </div>
              )}
              {msgs.map((msg, idx) => (
                <DebateMessageBubble key={`${roundNum}-${idx}`} message={msg} isComparison={isComparison} />
              ))}
            </div>
          ));
        })()}
        <div ref={messagesEndRef} />
      </div>

      {/* Verdict Confidence - How sure the mediator is about the winner */}
      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <Tooltip content="How decisive the verdict is. Higher means the evidence more clearly favors one direction. Above 80% is considered a strong, decisive outcome." position="top">
            <span className="text-text-muted cursor-help">
              Verdict Confidence (&gt;80% = decisive)
            </span>
          </Tooltip>
          <span className="text-amber-400 font-mono">{agreementScore}%</span>
        </div>
        <div className="h-2 bg-surface-light rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-500 to-green-500 rounded-full transition-all duration-500"
            style={{ width: `${agreementScore}%` }}
          />
        </div>
      </div>

      {/* Decisive Verdict Message - When confidence is high enough */}
      {agreementScore >= 80 && (
        <div className="mt-3 bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/30 text-center">
          <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            Decisive Verdict
          </div>
        </div>
      )}

      {/* Convergence Trajectory — shows how agreement evolved across rounds */}
      {isComplete && convergenceTrajectory && convergenceTrajectory.length >= 2 && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <Tooltip content="How agreement evolved across debate rounds. A rising line from low to high indicates genuine consensus through argument. A flat high line may indicate echo chamber risk." position="top">
              <span className="text-text-muted cursor-help">Convergence Trajectory</span>
            </Tooltip>
          </div>
          <ConvergenceSparkline data={convergenceTrajectory} />
        </div>
      )}

      {/* Echo Chamber Warning */}
      {isComplete && (echoChamberedDetected || (initialDivergence !== undefined && initialDivergence < 0.3 && initialDivergence > 0)) && (
        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-xs text-amber-300">
            Echo chamber risk — agents showed high initial agreement. Confidence has been adjusted.
          </span>
        </div>
      )}
    </div>
  );
}

/** Debate Message Bubble */
function DebateMessageBubble({ message, isComparison }: { message: DebateMessage; isComparison?: boolean }) {
  const isMediator = message.side === 'mediator';
  const isBull = message.side === 'bull';
  // In comparison mode with a label, use amber (Stock A / bull side) and blue (Stock B / bear side)
  const useComparisonColors = isComparison && !!message.label;

  return (
    <div
      className={cn(
        'rounded-lg p-2 text-xs',
        isMediator
          ? 'bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/20'
          : useComparisonColors
          ? isBull
            ? 'bg-amber-500/10 border border-amber-500/20 ml-4'
            : 'bg-blue-500/10 border border-blue-500/20 mr-4'
          : isBull
          ? 'bg-green-500/10 border border-green-500/20 ml-4'
          : 'bg-red-500/10 border border-red-500/20 mr-4'
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className={cn(
            'font-medium text-[10px] uppercase',
            isMediator
              ? 'text-amber-400'
              : useComparisonColors
              ? isBull ? 'text-amber-400' : 'text-blue-400'
              : isBull ? 'text-green-400' : 'text-red-400'
          )}
        >
          {isMediator ? 'MEDIATOR' : message.label ? `PRO-${message.label}` : isBull ? 'BULL' : 'BEAR'}
        </span>
        {message.round && <span className="text-text-muted text-[10px]">Round {message.round}</span>}
      </div>
      <p className="text-text-primary">{message.text}</p>
    </div>
  );
}

/** Mediator Compilation Node */
function MediatorCompilationNode({
  compilation,
  isActive,
  isComplete,
  isSmallScreen,
  isComparison,
  comparisonTickerA,
  comparisonTickerB,
}: {
  compilation?: MediatorCompilation;
  isActive: boolean;
  isComplete: boolean;
  isSmallScreen: boolean;
  isComparison?: boolean;
  comparisonTickerA?: string;
  comparisonTickerB?: string;
}) {
  return (
    <div
      className={cn(
        'relative border transition-all duration-500',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'p-3' : 'p-4',
        isActive
          ? 'from-violet-500/15 to-violet-600/5 border-violet-500/40 shadow-lg shadow-violet-500/10'
          : isComplete
          ? 'from-violet-500/10 to-violet-600/5 border-violet-500/30'
          : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('rounded-lg flex items-center justify-center bg-violet-500/20', isSmallScreen ? 'w-8 h-8' : 'w-10 h-10')}>
          {isActive ? (
            <Loader2 className={cn('animate-spin text-violet-400', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
          ) : (
            <Target className={cn('text-violet-400', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
          )}
        </div>
        <div className="flex-1">
          <div className={cn('font-mono uppercase tracking-wider text-violet-400', isSmallScreen ? 'text-xs' : 'text-sm')}>
            MEDIATOR COMPILATION
          </div>
          <p className="text-xs text-text-muted">(Higher Tier Model) - Synthesizes arguments and determines intensity</p>
        </div>
        <div className={cn('px-2 py-1 rounded text-xs', isComplete ? 'bg-violet-500/20 text-violet-400' : 'bg-surface-light text-text-muted')}>
          {isActive ? 'Compiling...' : isComplete ? 'Complete' : 'Waiting'}
        </div>
      </div>

      {compilation && (
        <div className="space-y-3">
          {/* Intensity Bar */}
          <div className="bg-surface/50 rounded-lg p-3">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className={cn('font-medium', isComparison ? 'text-amber-400' : 'text-green-400')}>
                {isComparison ? comparisonTickerA || 'A' : 'BULL'} {compilation.bullIntensity}%
              </span>
              <span className="text-text-muted">Case Intensity</span>
              <span className={cn('font-medium', isComparison ? 'text-blue-400' : 'text-red-400')}>
                {compilation.bearIntensity}% {isComparison ? comparisonTickerB || 'B' : 'BEAR'}
              </span>
            </div>
            <div className="h-3 bg-surface-light rounded-full overflow-hidden flex">
              <div
                className={cn('h-full transition-all duration-700', isComparison
                  ? 'bg-gradient-to-r from-amber-600 to-amber-400'
                  : 'bg-gradient-to-r from-green-600 to-green-400'
                )}
                style={{ width: `${compilation.bullIntensity}%` }}
              />
              <div
                className={cn('h-full transition-all duration-700', isComparison
                  ? 'bg-gradient-to-l from-blue-600 to-blue-400'
                  : 'bg-gradient-to-l from-red-600 to-red-400'
                )}
                style={{ width: `${compilation.bearIntensity}%` }}
              />
            </div>
          </div>

          {/* Arguments Grid */}
          <div className="grid md:grid-cols-2 gap-3">
            {/* Bull / Stock A Arguments */}
            <div className={cn('rounded-lg p-3 border', isComparison
              ? 'bg-amber-500/5 border-amber-500/20'
              : 'bg-green-500/5 border-green-500/20'
            )}>
              <div className="flex items-center gap-2 mb-2">
                {isComparison
                  ? <BarChart3 className="w-4 h-4 text-amber-400" />
                  : <TrendingUp className="w-4 h-4 text-green-400" />
                }
                <span className={cn('text-xs font-semibold', isComparison ? 'text-amber-400' : 'text-green-400')}>
                  {isComparison ? `${comparisonTickerA || 'A'} CASE` : 'BULL ARGUMENTS'}
                </span>
              </div>
              <ul className="space-y-1 text-xs text-text-primary">
                {compilation.bullArguments.map((arg, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <span className={cn('mt-0.5', isComparison ? 'text-amber-500' : 'text-green-500')}>+</span>
                    <span>{arg}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Bear / Stock B Arguments */}
            <div className={cn('rounded-lg p-3 border', isComparison
              ? 'bg-blue-500/5 border-blue-500/20'
              : 'bg-red-500/5 border-red-500/20'
            )}>
              <div className="flex items-center gap-2 mb-2">
                {isComparison
                  ? <BarChart3 className="w-4 h-4 text-blue-400" />
                  : <TrendingDown className="w-4 h-4 text-red-400" />
                }
                <span className={cn('text-xs font-semibold', isComparison ? 'text-blue-400' : 'text-red-400')}>
                  {isComparison ? `${comparisonTickerB || 'B'} CASE` : 'BEAR ARGUMENTS'}
                </span>
              </div>
              <ul className="space-y-1 text-xs text-text-primary">
                {compilation.bearArguments.map((arg, idx) => (
                  <li key={idx} className="flex items-start gap-1">
                    <span className={cn('mt-0.5', isComparison ? 'text-blue-500' : 'text-red-500')}>-</span>
                    <span>{arg}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Mediator Verdict */}
          <div className="bg-violet-500/10 rounded-lg p-3 border border-violet-500/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-violet-400" />
              <span className="text-xs font-semibold text-violet-400">MEDIATOR VERDICT</span>
            </div>
            <p className="text-sm text-text-primary">{compilation.verdict}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/** Kill Sheet Node - Final output */
function KillSheetNode({
  killSheet,
  onNewResearch,
  isSmallScreen,
  onExport,
}: {
  killSheet: FinancialKillSheet;
  onNewResearch?: () => void;
  isSmallScreen: boolean;
  onExport?: (format: ExportFormat) => Promise<void>;
}) {
  const [showRejected, setShowRejected] = useState(false);
  const isBullWin = killSheet.winning_side === 'bull';

  return (
    <div
      className={cn(
        'relative border-2 transition-all duration-500',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'p-3' : 'p-4',
        isBullWin
          ? 'from-emerald-500/15 to-green-600/5 border-emerald-500/40'
          : 'from-red-500/15 to-red-600/5 border-red-500/40'
      )}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={cn(
            'rounded-lg flex items-center justify-center',
            isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
            isBullWin ? 'bg-emerald-500/20' : 'bg-red-500/20'
          )}
        >
          <CheckCircle2 className={cn(isBullWin ? 'text-emerald-400' : 'text-red-400', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
        </div>
        <div className="flex-1">
          <Tooltip content="Final aggregated verdict after debate" position="top">
            <div className={cn('font-mono uppercase tracking-wider cursor-help', isBullWin ? 'text-emerald-400' : 'text-red-400', isSmallScreen ? 'text-xs' : 'text-sm')}>
              KILL SHEET
            </div>
          </Tooltip>
          <p className="text-xs text-text-muted">Final Aggregator - {killSheet.ticker} Analysis Complete</p>
        </div>
        <div className={cn('px-2 py-1 rounded text-xs font-mono', isBullWin ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400')}>
          {Math.round(killSheet.confidence * 100)}% confidence
        </div>
      </div>

      {/* Verdict */}
      <div
        className={cn(
          'rounded-lg p-4 mb-4 border',
          isBullWin ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'
        )}
      >
        <div className="flex items-center gap-2 mb-2">
          {isBullWin ? (
            <TrendingUp className="w-5 h-5 text-emerald-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-400" />
          )}
          <span className={cn('font-bold uppercase', isBullWin ? 'text-emerald-400' : 'text-red-400')}>
            {isBullWin ? 'BULL CASE WINS' : 'BEAR CASE WINS'}
          </span>
        </div>
        <p className="text-sm text-text-primary">{isBullWin ? killSheet.bull_thesis : killSheet.bear_thesis}</p>
      </div>

      {/* Kill Sheet Details Grid */}
      <div className="grid md:grid-cols-2 gap-3 text-sm mb-4">
        <div className="bg-surface/50 rounded-lg p-3">
          <span className="text-text-muted text-xs">Kill Sheet Reason</span>
          <p className="text-text-primary mt-1">{killSheet.kill_sheet_reason || killSheet.decisive_factor || '--'}</p>
        </div>
        <div className="bg-surface/50 rounded-lg p-3">
          <span className="text-text-muted text-xs">Caveats</span>
          <p className="text-text-primary mt-1">{killSheet.caveats || 'None specified'}</p>
        </div>
      </div>

      {/* Intensity Bars */}
      {killSheet.bull_intensity !== undefined && killSheet.bear_risk !== undefined && (
        <Tooltip content="Conviction strength for each side" position="top">
        <div className="bg-surface/50 rounded-lg p-3 mb-4 cursor-help">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-green-400 font-medium">Bull Intensity: {killSheet.bull_intensity}%</span>
            <span className="text-red-400 font-medium">Bear Risk: {killSheet.bear_risk}%</span>
          </div>
          <div className="flex gap-1 h-2">
            <div className="flex-1 bg-surface-light rounded-l overflow-hidden">
              <div className="h-full bg-green-500" style={{ width: `${killSheet.bull_intensity}%` }} />
            </div>
            <div className="flex-1 bg-surface-light rounded-r overflow-hidden">
              <div className="h-full bg-red-500 ml-auto" style={{ width: `${killSheet.bear_risk}%` }} />
            </div>
          </div>
        </div>
        </Tooltip>
      )}

      {/* Rejected Arguments */}
      {killSheet.rejected_arguments && killSheet.rejected_arguments.length > 0 && (
        <div className="border-t border-border pt-3">
          <button
            onClick={() => setShowRejected(!showRejected)}
            className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            {showRejected ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Rejected Arguments ({killSheet.rejected_arguments.length})
          </button>
          {showRejected && (
            <div className="mt-3 space-y-2">
              {killSheet.rejected_arguments.map((arg, idx) => (
                <div key={idx} className="bg-red-500/5 rounded-lg p-2 border border-red-500/20 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={cn('font-medium', arg.side === 'bull' ? 'text-green-400' : 'text-red-400')}>
                      {arg.agent_role}
                    </span>
                    <span className="text-text-muted">({arg.agent_model})</span>
                  </div>
                  <p className="text-text-secondary">{arg.argument_summary}</p>
                  <p className="text-red-400 mt-1 text-[10px]">Rejected: {arg.rejection_reason}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Data Sources - Audit Trail */}
      {killSheet.data_sources && Object.keys(killSheet.data_sources).length > 0 && (
        <div className="mt-3 bg-surface/50 rounded-lg p-3 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-semibold text-text-primary">Data Sources (Audit Trail)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(killSheet.data_sources).map(([key, value]) => {
              // Parse timestamp and format it nicely
              const timestamp = value;
              let formattedTime = timestamp;
              try {
                const date = new Date(timestamp);
                if (!isNaN(date.getTime())) {
                  formattedTime = date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                }
              } catch {
                // Keep original if parsing fails
              }
              return (
                <div
                  key={key}
                  className="flex items-center gap-2 px-2 py-1 bg-surface-light rounded text-xs"
                >
                  <span className="font-mono text-blue-400 uppercase">{key}</span>
                  <span className="text-text-muted">|</span>
                  <span className="flex items-center gap-1 text-text-secondary">
                    <Clock className="w-3 h-3" />
                    {formattedTime}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Export & Actions */}
      <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-3">
        {/* Export Menu */}
        {onExport && (
          <ExportMenu
            onExport={onExport}
            reportTitle={`${killSheet.ticker} Financial Analysis`}
          />
        )}
        {/* New Research Button */}
        {onNewResearch && (
          <button
            onClick={onNewResearch}
            className="flex-1 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg transition-colors text-sm font-medium"
          >
            Start New Analysis
          </button>
        )}
      </div>
    </div>
  );
}

/** Comparison Winner Banner - Shows recommended ticker in comparison mode */
function ComparisonWinnerBanner({
  comparisonKillSheet,
  isSmallScreen,
}: {
  comparisonKillSheet: ComparisonKillSheet;
  isSmallScreen: boolean;
}) {
  const winner = comparisonKillSheet.recommended_ticker;
  const loser = winner === comparisonKillSheet.ticker_a
    ? comparisonKillSheet.ticker_b
    : comparisonKillSheet.ticker_a;
  const confidencePercent = Math.round(comparisonKillSheet.confidence * 100);

  return (
    <div
      className={cn(
        'relative border-2 transition-all duration-500',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        'from-amber-500/15 to-amber-600/5 border-amber-500/40 shadow-lg shadow-amber-500/10',
        isSmallScreen ? 'p-4' : 'p-5'
      )}
    >
      {/* Winner Header */}
      <div className="text-center mb-4">
        <div className="text-xs font-mono uppercase tracking-wider text-amber-400 mb-2">
          COMPARISON VERDICT
        </div>
        <div className="flex items-center justify-center gap-3">
          <span className="text-2xl font-bold text-amber-400 font-mono">{winner}</span>
          <span className="text-text-muted text-sm">beats</span>
          <span className="text-lg text-text-secondary font-mono">{loser}</span>
        </div>
        <div className="text-sm text-text-secondary mt-1">
          {confidencePercent}% confidence
        </div>
      </div>

      {/* Head-to-Head Metrics */}
      {comparisonKillSheet.head_to_head_metrics && comparisonKillSheet.head_to_head_metrics.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-amber-400 mb-2 uppercase tracking-wider">
            Head-to-Head
          </div>
          <div className="space-y-1.5">
            {comparisonKillSheet.head_to_head_metrics.map((metric, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                <span className={cn(
                  'flex-1 text-right font-mono',
                  metric.advantage === 'a' ? 'text-green-400 font-bold' : 'text-text-secondary'
                )}>
                  {metric.ticker_a_value}
                </span>
                <span className="w-24 text-center text-text-muted truncate" title={metric.metric_name}>
                  {metric.metric_name}
                </span>
                <span className={cn(
                  'flex-1 font-mono',
                  metric.advantage === 'b' ? 'text-green-400 font-bold' : 'text-text-secondary'
                )}>
                  {metric.ticker_b_value}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-text-muted mt-1">
            <span className="flex-1 text-right">{comparisonKillSheet.ticker_a}</span>
            <span className="w-24 text-center">Metric</span>
            <span className="flex-1">{comparisonKillSheet.ticker_b}</span>
          </div>
        </div>
      )}

      {/* Comparison Reason */}
      {comparisonKillSheet.comparison_reason && (
        <div className="bg-surface/50 rounded-lg p-3 text-sm text-text-primary">
          {comparisonKillSheet.comparison_reason}
        </div>
      )}

      {/* Relative Insights Grid */}
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        {comparisonKillSheet.relative_valuation && (
          <div className="bg-surface/50 rounded-lg p-3">
            <span className="text-text-muted text-xs">Relative Valuation</span>
            <p className="text-text-primary mt-1 text-sm">{comparisonKillSheet.relative_valuation}</p>
          </div>
        )}
        {comparisonKillSheet.relative_moat && (
          <div className="bg-surface/50 rounded-lg p-3">
            <span className="text-text-muted text-xs">Competitive Moat</span>
            <p className="text-text-primary mt-1 text-sm">{comparisonKillSheet.relative_moat}</p>
          </div>
        )}
        {comparisonKillSheet.catalyst_comparison && (
          <div className="bg-surface/50 rounded-lg p-3">
            <span className="text-text-muted text-xs">Catalyst Comparison</span>
            <p className="text-text-primary mt-1 text-sm">{comparisonKillSheet.catalyst_comparison}</p>
          </div>
        )}
        {comparisonKillSheet.risk_comparison && (
          <div className="bg-surface/50 rounded-lg p-3">
            <span className="text-text-muted text-xs">Risk Comparison</span>
            <p className="text-text-primary mt-1 text-sm">{comparisonKillSheet.risk_comparison}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function FinancialLogicTree({
  state,
  query,
  className,
  onNewResearch,
  hideActions,
  onSwitchToGeneralResearch,
  onAnalyzeTicker,
}: FinancialLogicTreeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  // Modal state for agent output
  const [selectedAgent, setSelectedAgent] = useState<FinancialAgentProgress | null>(null);
  const [showAgentModal, setShowAgentModal] = useState(false);

  // Expanded view state - "fallen out" research blueprint
  const [isExpandedView, setIsExpandedView] = useState(false);

  const { phase, ticker, depth, hardDataSnapshot, bullAgents, bearAgents, debateMessages, debateRound, agreementScore, mediatorCompilation, killSheet, error, fallbackData, isComparison, comparisonTickerA, comparisonTickerB, comparisonSnapshotA, comparisonSnapshotB, convergenceTrajectory, initialDivergence, echoChamberedDetected } = state;

  // Handle agent click - show modal
  const handleAgentClick = useCallback((agent: FinancialAgentProgress) => {
    setSelectedAgent(agent);
    setShowAgentModal(true);
  }, []);

  // Handle export - passes all data for full audit trail
  const handleExport = useCallback(async (format: ExportFormat) => {
    if (!killSheet) return;

    const exportOptions: FinancialExportOptions = {
      query,
      ticker: ticker || killSheet.ticker,
      killSheet,
      bullAgents,
      bearAgents,
      anchoredSnapshot: hardDataSnapshot,
      mediatorCompilation,
    };

    await handleFinancialExport(format, exportOptions);
  }, [query, ticker, killSheet, bullAgents, bearAgents, hardDataSnapshot, mediatorCompilation]);

  // Phase visibility logic
  const showDataAnchor = phase !== 'idle';
  const showBullBear = phase !== 'idle' && phase !== 'fetching_data';
  const showDebate = ['debating', 'compiling', 'concluded'].includes(phase);
  const showMediator = ['compiling', 'concluded'].includes(phase);
  const showKillSheet = phase === 'concluded';

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full max-w-none bg-surface border border-border-subtle overflow-x-hidden shadow-2xl',
        isSmallScreen ? 'rounded-xl' : 'rounded-2xl',
        'flex flex-col',
        className
      )}
    >
      {/* Header */}
      <FinancialHeader depth={depth} ticker={ticker} phase={phase} isSmallScreen={isSmallScreen} isComparison={isComparison} />

      {/* Scrollable Content */}
      <div
        ref={scrollContentRef}
        className={cn(
          'flex-1 min-h-0 overflow-y-auto overflow-x-hidden',
          isSmallScreen ? 'p-3 space-y-3' : 'p-6 space-y-6'
        )}
      >
        {/* User Query Node */}
        <UserQueryNode query={query} isActive={phase === 'idle'} isSmallScreen={isSmallScreen} />

        {/* Warnings Banner (e.g., 3+ tickers limited to 2) */}
        {state.warnings && state.warnings.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              {state.warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-300">{w}</p>
              ))}
            </div>
          </div>
        )}

        {/* Connector to Data Anchor */}
        {showDataAnchor && <ConnectorLine />}

        {/* Data Anchor Node(s) - dual snapshots in comparison mode */}
        {showDataAnchor && isComparison ? (
          <div className="grid md:grid-cols-2 gap-3">
            <DataAnchorNode
              snapshot={comparisonSnapshotA || hardDataSnapshot}
              isActive={phase === 'fetching_data'}
              isComplete={phase !== 'fetching_data'}
              bullAgentCount={state.stockABullAgents?.length || Math.ceil(bullAgents.length / 2)}
              bearAgentCount={state.stockABearAgents?.length || Math.ceil(bullAgents.length / 2)}
              isSmallScreen={isSmallScreen}
            />
            <DataAnchorNode
              snapshot={comparisonSnapshotB}
              isActive={phase === 'fetching_data'}
              isComplete={phase !== 'fetching_data'}
              bullAgentCount={state.stockBBullAgents?.length || Math.ceil(bearAgents.length / 2)}
              bearAgentCount={state.stockBBearAgents?.length || Math.ceil(bearAgents.length / 2)}
              isSmallScreen={isSmallScreen}
            />
          </div>
        ) : showDataAnchor ? (
          <DataAnchorNode
            snapshot={hardDataSnapshot}
            isActive={phase === 'fetching_data'}
            isComplete={phase !== 'fetching_data'}
            bullAgentCount={bullAgents.length}
            bearAgentCount={bearAgents.length}
            isSmallScreen={isSmallScreen}
          />
        ) : null}

        {/* Connector branching to Bull/Bear */}
        {showBullBear && <ConnectorLine isBranching taskCount={2} />}

        {/* Bull/Bear Parallel Tasks */}
        {showBullBear && (
          <BullBearParallelTasks
            bullAgents={bullAgents}
            bearAgents={bearAgents}
            phase={phase}
            isSmallScreen={isSmallScreen}
            onAgentClick={handleAgentClick}
            isExpandedView={isExpandedView}
            onToggleExpanded={() => setIsExpandedView(!isExpandedView)}
            isComparison={isComparison}
            comparisonTickerA={comparisonTickerA}
            comparisonTickerB={comparisonTickerB}
          />
        )}

        {/* Connector merging to Debate */}
        {showDebate && <ConnectorLine isMerging taskCount={2} />}

        {/* Debate Node */}
        {showDebate && (
          <DebateNode
            messages={debateMessages}
            round={debateRound}
            agreementScore={agreementScore}
            isActive={phase === 'debating'}
            isComplete={phase === 'compiling' || phase === 'concluded'}
            isSmallScreen={isSmallScreen}
            disableAutoScroll={hideActions}
            isComparison={isComparison}
            comparisonTickerA={comparisonTickerA}
            comparisonTickerB={comparisonTickerB}
            convergenceTrajectory={convergenceTrajectory}
            initialDivergence={initialDivergence}
            echoChamberedDetected={echoChamberedDetected}
          />
        )}

        {/* Connector to Mediator */}
        {showMediator && <ConnectorLine />}

        {/* Mediator Compilation Node */}
        {showMediator && (
          <MediatorCompilationNode
            compilation={mediatorCompilation}
            isActive={phase === 'compiling'}
            isComplete={phase === 'concluded'}
            isSmallScreen={isSmallScreen}
            isComparison={isComparison}
            comparisonTickerA={comparisonTickerA}
            comparisonTickerB={comparisonTickerB}
          />
        )}

        {/* Connector to Kill Sheet */}
        {showKillSheet && <ConnectorLine />}

        {/* Kill Sheet Node - comparison mode shows winner banner */}
        {showKillSheet && killSheet && (
          <>
            {isComparison && state.comparisonKillSheet && (
              <ComparisonWinnerBanner
                comparisonKillSheet={state.comparisonKillSheet}
                isSmallScreen={isSmallScreen}
              />
            )}
            <KillSheetNode
              killSheet={killSheet}
              onNewResearch={hideActions ? undefined : onNewResearch}
              isSmallScreen={isSmallScreen}
              onExport={hideActions ? undefined : handleExport}
            />
          </>
        )}

        {/* Fallback State - No ticker identified */}
        {fallbackData && (
          <NoTickerFallback
            fallbackData={fallbackData}
            originalQuery={query}
            onSwitchToGeneralResearch={onSwitchToGeneralResearch}
            onAnalyzeTicker={onAnalyzeTicker}
            isSmallScreen={isSmallScreen}
          />
        )}

        {/* Error State (non-fallback errors) */}
        {error && !fallbackData && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Agent Output Modal */}
      {showAgentModal && selectedAgent && (
        <FinancialAgentModal
          agent={selectedAgent}
          onClose={() => {
            setShowAgentModal(false);
            setSelectedAgent(null);
          }}
        />
      )}
    </div>
  );
}

/** Financial Agent Output Modal - Shows full agent response */
function FinancialAgentModal({
  agent,
  onClose,
}: {
  agent: FinancialAgentProgress;
  onClose: () => void;
}) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const providerColor = PROVIDER_TEXT_COLORS[agent.provider] || 'text-text-secondary';
  const isBull = agent.side === 'bull';

  // Get color based on side
  const sideColor = isBull ? '#22c55e' : '#ef4444';

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface border border-border rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-surface/50">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${sideColor}20` }}
            >
              <span className={providerColor}>{getProviderLogo(agent.provider, 20)}</span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">{agent.roleName}</h2>
              <div className="flex items-center gap-3 text-xs text-text-muted">
                <span className="font-mono">{agent.provider}</span>
                <span className={cn('font-mono', isBull ? 'text-green-400' : 'text-red-400')}>
                  {isBull ? 'BULL' : 'BEAR'}
                </span>
                {agent.latency !== undefined && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {(agent.latency / 1000).toFixed(1)}s
                  </span>
                )}
                {agent.citationCount !== undefined && (
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {agent.citationCount} citations
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex items-center gap-2 mb-3 text-xs text-text-muted">
            <FileText className="w-3 h-3" />
            <span>Agent {agent.index + 1} - {agent.model}</span>
            {agent.status === 'complete' ? (
              <span className="flex items-center gap-1 text-emerald-500">
                <CheckCircle2 className="w-3 h-3" />
                Complete
              </span>
            ) : (
              <span className="flex items-center gap-1 text-amber-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                Processing
              </span>
            )}
          </div>

          {/* Output content - render as markdown (full output) */}
          <div className="bg-surface/50 rounded-xl p-4 border border-border">
            {agent.output ? (
              <MarkdownContent content={agent.output} />
            ) : (
              <p className="text-text-muted italic">No output available yet</p>
            )}
          </div>

          {/* Sources Section - for audit trail */}
          {agent.citations && agent.citations.length > 0 && (
            <div className="bg-surface/50 rounded-xl p-4 border border-border">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-blue-400" />
                <h3 className="text-sm font-semibold text-text-primary">Sources ({agent.citations.length})</h3>
              </div>
              <div className="space-y-2">
                {agent.citations.map((citation, idx) => (
                  <div
                    key={`${citation.id}-${idx}`}
                    className="flex items-start gap-3 p-2 bg-surface-light/50 rounded-lg text-xs"
                  >
                    {/* Citation ID badge */}
                    <span className="flex-shrink-0 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded font-mono text-xs">
                      {citation.id}
                    </span>
                    <div className="flex-1 min-w-0">
                      {/* Source type and title */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-text-secondary font-mono text-xs">
                          {citation.source_type}
                        </span>
                        <span className="text-text-primary">{citation.title}</span>
                      </div>
                      {/* Timestamp and URL */}
                      <div className="flex items-center gap-3 mt-1 text-text-muted">
                        {citation.published_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(citation.published_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        )}
                        {citation.url && (
                          <a
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Source
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-surface/30">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-surface-light hover:bg-surface-raised text-text-primary rounded-lg transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

